import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Typography,
  IconButton,
  Stack,
  TableSortLabel,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import { format } from "date-fns";
import { formatCurrency, formatDate, formatDateTime, formatPairPrice, formatUsd } from "../../utils/formatters";
import {
  IPosition,
  IManualMovement,
  ManualMovementType,
} from "../../shared/types";
import EditIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/DeleteRounded";
import SaveIcon from "@mui/icons-material/SaveRounded";
import CancelIcon from "@mui/icons-material/CancelRounded";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { visuallyHidden } from "@mui/utils";
import { useResponsive } from "../../hooks/useResponsive";
import { SurfaceCard } from "../ui/SurfaceCard";
import { SectionHeader } from "../ui/SectionHeader";
import { StatusPill } from "../ui/StatusPill";
import { tokens } from "../../theme";

// Define a type for the data rows, which can be either IPosition or IManualMovement
type DataRow = IPosition | IManualMovement;

interface DataTableProps {
  title: string;
  count: number;
  columns: string[];
  data: DataRow[];
  type: "positions" | "movements";
  onDeleteMovement?: (id: string) => void;
  onEditMovement?: (movement: IManualMovement) => void;
  decimals?: number;
}

interface HeadCell {
  id: string;
  label: string;
  numeric: boolean;
  align: "left" | "right" | "center";
  sortable: boolean;
  format?: (value: any) => string;
}

type Order = "asc" | "desc";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string | boolean },
  b: { [key in Key]: number | string | boolean }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

/** Fees in USD, shown like every card figure (formatUsd: "$302", "$1,033"; cents only under $100). */
const positionFeesUSD = (row: IPosition) =>
  row.feesObtainedToken0 * row.balanceSnapshotEnd.token0Price +
  row.feesObtainedToken1 * row.balanceSnapshotEnd.token1Price;

const positionEndPrice = (row: IPosition) =>
  row.isReverse
    ? row.balanceSnapshotEnd.token0Price !== 0
      ? row.balanceSnapshotEnd.token1Price / row.balanceSnapshotEnd.token0Price
      : 0
    : row.balanceSnapshotEnd.token1Price !== 0
    ? row.balanceSnapshotEnd.token0Price / row.balanceSnapshotEnd.token1Price
    : 0;

/**
 * Open as the backend counts it: open, or a close skipped because the position was already closed
 * (`SKIPPED_ALREADY_CLOSED`), which the backend still treats as open. One rule for the status pill,
 * the exit price, the closed date and the "N open" count.
 */
const isEffectivelyOpen = (row: IPosition): boolean => row.isOpen || row.closeTxHash === "SKIPPED_ALREADY_CLOSED";

/** Sort key for position columns, including the derived price/fee columns. */
const positionSortValue = (row: IPosition, key: string): number | string | boolean => {
  if (key === "isOpen") return isEffectivelyOpen(row);
  // Open positions have no exit yet: they sort together, like their "—" cells.
  if (key === "endDate" || key === "endPrice") {
    if (isEffectivelyOpen(row)) return 0;
    return key === "endPrice" ? positionEndPrice(row) : row.endDate ?? 0;
  }
  if (key === "fees") return positionFeesUSD(row);
  return (row as any)[key] ?? 0;
};

const numericCell = {
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap" as const,
};

const PositionStatus: React.FC<{ open: boolean }> = ({ open }) => (
  <StatusPill tone={open ? "success" : "neutral"} label={open ? "Open" : "Closed"} size="sm" pulsing={open} />
);

const PositionRow: React.FC<{ row: IPosition; decimals: number }> = ({
  row,
  decimals,
}) => {
  const feesUSD = positionFeesUSD(row);
  const endPrice = positionEndPrice(row);
  const open = isEffectivelyOpen(row);

  return (
    <TableRow hover sx={{ "&:last-of-type td": { borderBottom: 0 } }}>
      <TableCell sx={{ color: "text.secondary", ...numericCell }}>
        {formatDateTime(row.date)}
      </TableCell>
      <TableCell sx={{ color: "text.secondary", ...numericCell }}>
        {!open && row.endDate ? formatDateTime(row.endDate) : "—"}
      </TableCell>
      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
        {row.token0Symbol} / {row.token1Symbol}
      </TableCell>
      <TableCell align="right" sx={{ ...numericCell, color: "text.secondary" }}>
        {row.positionId}
      </TableCell>
      <TableCell align="center" sx={numericCell}>
        {row.roleIndex ?? "—"}
      </TableCell>
      <TableCell align="center">
        <PositionStatus open={open} />
      </TableCell>
      <TableCell align="right" sx={numericCell}>
        {formatPairPrice(row.entryPrice, row.token0Symbol, row.token1Symbol, decimals)}
      </TableCell>
      <TableCell align="right" sx={numericCell}>
        {open ? "—" : formatPairPrice(endPrice, row.token0Symbol, row.token1Symbol, decimals)}
      </TableCell>
      <TableCell align="right" sx={{ ...numericCell, fontWeight: 650, color: feesUSD > 0 ? "success.main" : "text.secondary" }}>
        {formatUsd(feesUSD)}
      </TableCell>
    </TableRow>
  );
};

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** Position lifetime: "8d", or "5h" under a day. */
const formatSpan = (ms: number): string => {
  const hours = Math.round(ms / HOUR_MS);
  if (hours < 1) return "<1h";
  if (hours < 24) return `${hours}h`;
  return `${Math.round(ms / DAY_MS)}d`;
};

/** Short card date ("Sep 14"); the year is added ("Dec 28 '25") unless it is one of `impliedYears`. */
const shortDate = (ts: number, ...impliedYears: number[]): string =>
  format(ts, impliedYears.includes(new Date(ts).getFullYear()) ? "MMM d" : "MMM d ''yy");

/** Two-line position card for phones and tablets. */
const PositionCard: React.FC<{ row: IPosition; decimals: number }> = ({ row, decimals }) => {
  const feesUSD = positionFeesUSD(row);
  const thisYear = new Date().getFullYear();
  const open = isEffectivelyOpen(row);
  const end = open ? Date.now() : row.endDate;
  const closedLabel = open
    ? "now"
    : row.endDate
    ? shortDate(row.endDate, thisYear, new Date(row.date).getFullYear())
    : "—";
  const price = (v: number) => formatPairPrice(v, row.token0Symbol, row.token1Symbol, decimals);
  const prices = open ? `entry ${price(row.entryPrice)}` : `${price(row.entryPrice)} → ${price(positionEndPrice(row))}`;
  const details = [
    `${shortDate(row.date, thisYear)} → ${closedLabel}`,
    end && end > row.date ? formatSpan(end - row.date) : null,
    prices,
  ].filter((part): part is string => !!part);

  return (
    <Box sx={{ px: { xs: 2.5, md: 3 }, py: 1.75 }}>
      {/* The id never truncates: on the narrowest phones the "Fees" word goes, then the amount wraps. */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 2, rowGap: 0.25 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 650, ...numericCell }}>#{row.positionId}</Typography>
          <PositionStatus open={open} />
        </Stack>
        <Typography component="div" sx={{ ml: "auto", fontSize: "0.75rem", color: "text.secondary", ...numericCell }}>
          <Box component="span" sx={{ "@media (max-width: 359.95px)": { display: "none" } }}>
            Fees{" "}
          </Box>
          <Box
            component="span"
            sx={{ fontSize: "0.875rem", fontWeight: 700, color: feesUSD > 0 ? tokens.positive : "text.secondary" }}
          >
            {formatUsd(feesUSD)}
          </Box>
        </Typography>
      </Box>
      {/* Wraps only between parts (never mid-date or mid-price), keeping the "·" at the end of a line. */}
      <Typography
        component="div"
        sx={{ mt: 0.5, fontSize: "0.75rem", lineHeight: 1.45, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}
      >
        {details.map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 && "\u00A0· "}
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              {part}
            </Box>
          </React.Fragment>
        ))}
      </Typography>
    </Box>
  );
};

const MovementRow: React.FC<{
  row: IManualMovement;
  onDelete: (id: string) => void;
  onEdit: (movement: IManualMovement) => void;
}> = ({ row, onDelete, onEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedMovement, setEditedMovement] = useState<IManualMovement>({
    ...row,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    try {
      await onEdit(editedMovement);
      setSnackbarMessage("Movement updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsEditing(false);
    } catch (error: any) {
      setSnackbarMessage(`Error updating movement: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedMovement({ ...row }); // Reset changes
  };

  const handleDeleteClick = async () => {
    if (window.confirm("Are you sure you want to delete this movement?")) {
      try {
        await onDelete(row.id!); // Ensure id is not undefined
        setSnackbarMessage("Movement deleted successfully! ");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } catch (error: any) {
        setSnackbarMessage(`Error deleting movement: ${error.message}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDateChange = (newValue: Date | null) => {
    setEditedMovement((prev) => ({
      ...prev,
      date: newValue ? format(newValue, "yyyy-MM-dd") : "",
    }));
  };

  return (
    <TableRow>
      <TableCell>{editedMovement.botId}</TableCell>
      <TableCell>
        {isEditing ? (
          <TextField
            select
            value={editedMovement.movementType}
            onChange={(e) =>
              setEditedMovement((prev) => ({
                ...prev,
                movementType: e.target.value as ManualMovementType,
              }))
            }
            size="small"
            variant="outlined"
            margin="dense"
          >
            <MenuItem value="DEPOSIT">Deposit</MenuItem>
            <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
            <MenuItem value="MANUAL_PREMIUM">Manual Premium</MenuItem>
          </TextField>
        ) : (
          editedMovement.movementType
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={new Date(editedMovement.date)}
              onChange={handleDateChange}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  size: "small",
                  variant: "outlined",
                  margin: "dense",
                },
              }}
            />
          </LocalizationProvider>
        ) : (
          formatDate(new Date(editedMovement.date).getTime())
        )}
      </TableCell>
      <TableCell align="right">
        {isEditing ? (
          <TextField
            value={editedMovement.amountUSD}
            onChange={(e) =>
              setEditedMovement((prev) => ({
                ...prev,
                amountUSD: parseFloat(e.target.value),
              }))
            }
            type="number"
            size="small"
            variant="outlined"
            margin="dense"
            InputProps={{ startAdornment: <Typography>$</Typography> }}
          />
        ) : (
          formatCurrency(editedMovement.amountUSD)
        )}
      </TableCell>
      <TableCell align="right">
        {isEditing ? (
          <Stack direction="row" spacing={1}>
            <IconButton onClick={handleSaveClick} color="primary">
              <SaveIcon sx={{ fontSize: 26 }} />
            </IconButton>
            <IconButton onClick={handleCancelClick} color="error">
              <CancelIcon sx={{ fontSize: 26 }} />
            </IconButton>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1}>
            <IconButton onClick={handleEditClick} color="info">
              <EditIcon sx={{ fontSize: 26 }} />
            </IconButton>
            <IconButton onClick={handleDeleteClick} color="error">
              <DeleteIcon sx={{ fontSize: 26 }} />
            </IconButton>
          </Stack>
        )}
      </TableCell>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </TableRow>
  );
};

const DataTable: React.FC<DataTableProps> = ({
  title,
  count,
  data,
  type,
  onDeleteMovement,
  onEditMovement,
  decimals,
}) => {
  const { isMobile, isTablet } = useResponsive();
  // Positions read better as cards until the full table fits (≥1200px).
  const positionCards = type === "positions" && (isMobile || isTablet);
  const [page, setPage] = useState(0);
  // Position cards are tall on phones: start with a shorter page.
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>(type === "positions" ? "desc" : "asc");
  const [orderBy, setOrderBy] = useState<string>(type === "positions" ? "date" : "");

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const headCells: HeadCell[] = useMemo(() => {
    if (type === "positions") {
      return [
        { id: "date", numeric: false, label: "Opened", align: "left", sortable: true },
        { id: "endDate", numeric: false, label: "Closed", align: "left", sortable: true },
        { id: "pair", numeric: false, label: "Pair", align: "left", sortable: false },
        { id: "positionId", numeric: true, label: "Position", align: "right", sortable: true },
        { id: "roleIndex", numeric: true, label: "Role", align: "center", sortable: true },
        { id: "isOpen", numeric: false, label: "Status", align: "center", sortable: true },
        { id: "entryPrice", numeric: true, label: "Entry", align: "right", sortable: true },
        { id: "endPrice", numeric: true, label: "Exit", align: "right", sortable: true },
        { id: "fees", numeric: true, label: "Fees (USD)", align: "right", sortable: true },
      ];
    }
    return [
      { id: "botId", numeric: false, label: "Bot ID", align: "left", sortable: true },
      { id: "movementType", numeric: false, label: "Type", align: "left", sortable: true },
      { id: "date", numeric: false, label: "Date", align: "left", sortable: true },
      { id: "amountUSD", numeric: true, label: "Amount (USD)", align: "right", sortable: true, format: formatCurrency },
      { id: "actions", numeric: false, label: "Actions", align: "right", sortable: false },
    ];
  }, [type]);

  const sortedData = useMemo(() => {
    if (!orderBy || data.length === 0) return data;
    if (type === "positions") {
      const dir = order === "desc" ? -1 : 1;
      return stableSort(data as IPosition[], (a, b) => {
        const va = positionSortValue(a, orderBy);
        const vb = positionSortValue(b, orderBy);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }
    return stableSort(data, getComparator(order, orderBy as keyof DataRow));
  }, [data, order, orderBy, type]);

  const visibleRows =
    rowsPerPage > 0 ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : sortedData;
  const openCount = type === "positions" ? (data as IPosition[]).filter(isEffectivelyOpen).length : 0;

  const pagination = (
    <TablePagination
      rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
      component="div"
      count={data.length}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      labelRowsPerPage={isMobile ? "Rows" : "Rows per page"}
      sx={{
        borderTop: `1px solid ${tokens.borderSubtle}`,
        // Touch: the rows select and the page arrows get full 44px targets.
        "@media (pointer: coarse)": {
          "& .MuiTablePagination-toolbar": { minHeight: 56 },
          "& .MuiTablePagination-input": { minHeight: 44, display: "inline-flex", alignItems: "center" },
          "& .MuiTablePagination-select": { minHeight: "unset", py: 1.25 },
        },
      }}
    />
  );

  return (
    <SurfaceCard padding="none" sx={{ overflow: "hidden" }}>
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 } }}>
        <SectionHeader
          title={title}
          icon={TableRowsRoundedIcon}
          subtitle={
            type === "positions"
              ? `${count} positions · ${openCount} open`
              : `${count} movements`
          }
        />
      </Box>

      {data.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">No data available.</Typography>
        </Box>
      ) : positionCards ? (
        <Box sx={{ borderTop: `1px solid ${tokens.borderSubtle}` }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
              "& > *": { borderBottom: `1px solid ${tokens.borderSubtle}` },
              "& > *:nth-of-type(odd)": { borderRight: { md: `1px solid ${tokens.borderSubtle}` } },
            }}
          >
            {(visibleRows as IPosition[]).map((row) => (
              <PositionCard key={`${row.botId}-${row.positionId}`} row={row} decimals={decimals || 4} />
            ))}
          </Box>
          {pagination}
        </Box>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 640 }}>
            <Table stickyHeader aria-label={title} size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.align}
                      sortDirection={orderBy === headCell.id ? order : false}
                      sx={{ py: 1.25, whiteSpace: "nowrap", "&:first-of-type": { pl: { xs: 2, md: 3 } }, "&:last-of-type": { pr: { xs: 2, md: 3 } } }}
                    >
                      {headCell.sortable ? (
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : "asc"}
                          onClick={(event) => handleRequestSort(event, headCell.id)}
                        >
                          {headCell.label}
                          {orderBy === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === "desc" ? "sorted descending" : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  "& td": { py: 1.25, fontSize: "0.8125rem" },
                  "& td:first-of-type": { pl: { xs: 2, md: 3 } },
                  "& td:last-of-type": { pr: { xs: 2, md: 3 } },
                }}
              >
                {visibleRows.map((row) =>
                  type === "positions" ? (
                    <PositionRow
                      key={`${(row as IPosition).botId}-${(row as IPosition).positionId}`}
                      row={row as IPosition}
                      decimals={decimals || 4}
                    />
                  ) : (
                    <MovementRow
                      key={(row as IManualMovement).id}
                      row={row as IManualMovement}
                      onDelete={onDeleteMovement!}
                      onEdit={onEditMovement!}
                    />
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination}
        </>
      )}
    </SurfaceCard>
  );
};

export default DataTable;
